// Internal modules
import { EMEA_LITE_COUNTRIES, GEO_REGIONS } from './constants';

const globals = Object.assign(
  {
    accessToken: {
      type: null,
      value: null,
    },
    cancelUrl: '',
    contentHash: null,
    correlationId: '',
    experiments: {},
    fallback: {
      endpoint: null,
      redirectOnError: null,
    },
    features: {},
    initialGqlData: {
      checkoutSession: {
        cart: {
          amounts: {
            total: {},
          },
          billingType: null,
          shippingMethods: [],
        },
        creditMeta: {
          upstreamCreditPresentment: false,
        },
        flags: {},
        merchant: {},
      },
      elmoExperiment: {
        treatments: [],
      },
      localeMetadata: {
        currencyCode: '',
      },
    },
    isBasl: false,
    isInternalRequest: false,
    locality: {
      country: 'US',
      language: 'en',
    },
    uri: {
      fallbackEndpoint: null,
      hermesEndpoint: null,
      loggerEndpoint: null,
      queryEndpoint: null,
    },
    user: {
      ecToken: null,
    },
    useraction: null,
  },
  window.__INITIAL_DATA__,
);

// Initial data will be available by calling 'getGlobals()'
const getGlobals = () => globals;

const getAccessToken = () => globals.accessToken;
const setAccessToken = accessTokenObj => {
  globals.accessToken = accessTokenObj;
};
const clearAccessToken = () => {
  globals.accessToken = {
    type: null,
    value: null,
  };
};

const getLocale = () => {
  const { country, language } = globals.locality;
  return {
    country,
    language,
    locale: `${language}_${country}`,
  };
};

const getToken = () => globals.user.ecToken;

/**
 * @function updateLocale
 * @param {object} updatedLocale { country, language }
 * @description Updates the locality in the globals object
 */
const updateLocale = updatedLocale => {
  // Spreading the `globals.locality` to allow this function to be used when
  // only updating one field. e.g. The user changes the language selection
  globals.locality = {
    ...globals.locality,
    ...updatedLocale,
  };
};

const getCheckoutSessionFlags = () => {
  return globals.initialGqlData.checkoutSession.flags;
};

const getGeoRegion = () => {
  const { localeMetadata } = globals.initialGqlData;
  const { businessRegions: regions } = localeMetadata || {};

  if (!Array.isArray(regions)) {
    return null;
  }

  const geoRegion = regions.find(region => GEO_REGIONS.includes(region));

  return geoRegion || null;
};

const getUnifiedLoginContextData = () => {
  const { unifiedLoginContext } = globals.initialGqlData;
  return unifiedLoginContext?.experienceMetadata?.onboardingData || {};
};

const isEmeaLiteCountry = () => {
  const { country } = getLocale();

  return EMEA_LITE_COUNTRIES.includes(country);
};

const isRtlLang = () => globals.locality.directionality === 'rtl';

const isUserActionCommit = () => globals.useraction === 'commit';

const updateExperiments = () => {
  const { treatments } = globals.initialGqlData.elmoExperiment;

  globals.experiments = treatments.reduce((accum, exp) => {
    const factors = exp.factors?.reduce((accum, { key, value }) => {
      accum[key] = value;
      return accum;
    }, {});

    accum[exp.experimentName] = {
      ...exp,
      factors: factors || null,
    };
    return accum;
  }, {});
};

// Set the initial experiments
updateExperiments();

export {
  clearAccessToken,
  getAccessToken,
  getCheckoutSessionFlags,
  getGeoRegion,
  getGlobals,
  getLocale,
  getToken,
  getUnifiedLoginContextData,
  isEmeaLiteCountry,
  isRtlLang,
  isUserActionCommit,
  setAccessToken,
  updateExperiments,
  updateLocale,
};
